import React, { useMemo, useCallback, useEffect } from "react";
import { Box, Stack, Typography, Chip, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import useCampuses from "../hooks/useCampuses.js";
import TimetableBoard from "./TimetableBoard";
import { isWeekendDate, buildLaneData } from "../utils/timetableUtils.js";

const normaliseCampus = (s) =>
  String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export default function TimetablePage({
  selectedDate,
  setSelectedDate,
  selectedCampusId,
  setSelectedCampusId,
  onOpenBedrooms,
  onOpenTransfers,
  onOpenTransport,
  onOpenClasses,
  onOpenActivities,
  onOpenStaffSchedule,
  onBack,
  backLabel,
  kiosk = false,
  campusSlug,
  onLogout,
}) {
  const { campuses = [], loading: campusesLoading } = useCampuses();

  // Kiosk: resolve the campus from the account slug (e.g. "dollar" -> Dollar)
  // and lock the board to it.
  const kioskCampus = useMemo(
    () =>
      kiosk && campusSlug
        ? campuses.find(
            (c) => normaliseCampus(c.name) === normaliseCampus(campusSlug)
          )
        : null,
    [kiosk, campusSlug, campuses]
  );

  useEffect(() => {
    if (kioskCampus && String(kioskCampus.id) !== String(selectedCampusId)) {
      setSelectedCampusId(String(kioskCampus.id));
    }
  }, [kioskCampus, selectedCampusId, setSelectedCampusId]);

  const items = [];
  const loading = false;
  const error = "";

  const weekend = useMemo(
    () => isWeekendDate(selectedDate?.toDate?.() || selectedDate),
    [selectedDate]
  );

  const lanes = useMemo(() => {
    return buildLaneData(items, { weekend });
  }, [items, weekend]);

  const handleRefresh = useCallback(() => {}, []);

  const selectedCampus = campuses.find(
    (c) => String(c.id) === String(selectedCampusId)
  );

  const visibleCampusCount = selectedCampusId ? 1 : campuses.length;

  if (kiosk) {
    // Account slug didn't match any campus — surface it instead of cycling an
    // empty/all-campuses board.
    if (!campusesLoading && campuses.length > 0 && !kioskCampus) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            Couldn't match a campus for this TV account
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
            No campus matches "{campusSlug}". Check the account email
            (expected &lt;campus&gt;.tv@…).
          </Typography>
          {onLogout && (
            <Button variant="outlined" onClick={onLogout}>
              Log out
            </Button>
          )}
        </Box>
      );
    }

    return (
      <TimetableBoard
        kiosk
        onLogout={onLogout}
        campuses={campuses}
        campusesLoading={campusesLoading}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedCampusId={selectedCampusId}
        setSelectedCampusId={setSelectedCampusId}
        lanes={lanes}
        loading={loading}
        error={error}
        onRefresh={handleRefresh}
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {onBack && (
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mb: 1 }}
        >
          {backLabel || "Back"}
        </Button>
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack>
          <Typography variant="h5" fontWeight={800}>
            Timetable
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {selectedDate.format("dddd D MMM YYYY")}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Chip label={weekend ? "Weekend" : "Weekday"} />
          <Chip label={selectedCampus ? selectedCampus.name : "All campuses"} />
          <Chip label={`Groups ${items.length}`} />
          <Chip label={`Campuses ${visibleCampusCount}`} />
        </Stack>
      </Stack>

      <TimetableBoard
        campuses={campuses}
        campusesLoading={campusesLoading}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        selectedCampusId={selectedCampusId}
        setSelectedCampusId={setSelectedCampusId}
        lanes={lanes}
        loading={loading}
        error={error}
        onRefresh={handleRefresh}
        onOpenBedrooms={onOpenBedrooms}
        onOpenTransfers={onOpenTransfers}
        onOpenTransport={onOpenTransport}
        onOpenClasses={onOpenClasses}
        onOpenActivities={onOpenActivities}
        onOpenStaffSchedule={onOpenStaffSchedule}
      />
    </Box>
  );
}